import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, Loader2, RefreshCw, Check, X, History, ChevronDown, ChevronRight,
  Lock, CircleCheck, CircleX, Clock, PlayCircle, UserCheck, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const STATUS = {
  pending_approval: { label: 'Pending approval', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300', Icon: CircleCheck },
  rejected: { label: 'Rejected', cls: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300', Icon: CircleX },
  actioned: { label: 'Actioned', cls: 'bg-sky-500/10 border-sky-500/30 text-sky-600 dark:text-sky-300', Icon: UserCheck },
};
const FILTERS = ['', 'pending_approval', 'approved', 'rejected', 'actioned'];

export default function RecommendationApprovalsPage() {
  const { user } = useAuth();
  const canManage = useCan('measures:certify');
  const [approvals, setApprovals] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(new Set());
  const [notes, setNotes] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [searchParams] = useSearchParams();
  const focusApprovalId = searchParams.get('approval');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getApprovals('', statusFilter);
      setApprovals(r.approvals || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: when arriving via a "View in approvals" link, auto-expand and
  // scroll the targeted recommendation into view (Approval Inbox Link).
  useEffect(() => {
    if (!focusApprovalId || loading || approvals.length === 0) return;
    setExpanded((prev) => new Set(prev).add(focusApprovalId));
    const el = document.querySelector(`[data-testid="appr-item-${focusApprovalId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusApprovalId, loading, approvals]);

  const withBusy = async (key, fn, okMsg) => {
    setBusy((p) => new Set(p).add(key));
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setBusy((p) => { const s = new Set(p); s.delete(key); return s; }); }
  };

  const decide = (a, decision) => withBusy(`${a.approval_id}-${decision}`,
    () => api.decideApproval(a.approval_id, decision, notes[a.approval_id] || ''),
    decision === 'approve' ? 'Approved' : 'Rejected');
  const action = (a) => withBusy(`${a.approval_id}-action`,
    () => api.markApprovalActioned(a.approval_id, notes[a.approval_id] || ''), 'Marked actioned');

  const toggleExpand = (id) => setExpanded((p) => {
    const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="approvals-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" /> Recommendation Approvals
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Material recommendations need a named human approver + full audit trail before anyone acts. Four-eyes: you can&apos;t approve your own request.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="gap-1 text-xs h-8" data-testid="appr-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid="appr-guardrail">
        <Lock className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
        <p className="text-[11px] text-emerald-700 dark:text-emerald-300">Nothing auto-executes. Approval records a governed decision; a human still carries out the action and marks it done.</p>
      </div>

      <div className="flex items-center gap-1 flex-wrap" data-testid="appr-filters">
        {FILTERS.map((f) => (
          <button key={f || 'all'} onClick={() => setStatusFilter(f)}
            className={`px-2.5 py-1 rounded-md text-[11px] border ${statusFilter === f ? 'border-primary text-foreground bg-primary/10' : 'border-border/40 text-muted-foreground'}`}
            data-testid={`appr-filter-${f || 'all'}`}>
            {f ? (STATUS[f]?.label || f) : 'All'}{f && counts[f] != null ? ` (${counts[f]})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : approvals.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="appr-empty">
          No approval requests{statusFilter ? ` in "${STATUS[statusFilter]?.label}"` : ''}. Submit a material recommendation from the Recommendation Rules page.
        </div>
      ) : (
        <div className="space-y-2" data-testid="appr-list">
          {approvals.map((a) => {
            const st = STATUS[a.status] || STATUS.pending_approval;
            const StIcon = st.Icon;
            const isRequester = (a.requested_by || {}).user_id === user?.user_id;
            const open = expanded.has(a.approval_id);
            const rec = a.recommendation || {};
            return (
              <div key={a.approval_id} className={`rounded-xl border p-3 space-y-2 ${a.approval_id === focusApprovalId ? 'border-violet-500 ring-2 ring-violet-500/40' : a.status === 'pending_approval' ? 'border-amber-500/30' : 'border-border/40'} bg-card/30`}
                data-testid={`appr-item-${a.approval_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <Badge variant="outline" className={`text-[9px] gap-1 ${st.cls}`} data-testid={`appr-status-${a.approval_id}`}>
                        <StIcon className="w-3 h-3" />{st.label}
                      </Badge>
                      <Badge variant="outline" className="text-[9px] border-rose-500/40 text-rose-600 dark:text-rose-300">material</Badge>
                      <Badge variant="outline" className="text-[9px] border-border/40 text-muted-foreground capitalize">{rec.category}</Badge>
                      <span className="text-[10px] text-muted-foreground">{a.rule_name}</span>
                    </div>
                    <p className="text-sm text-foreground font-medium">{rec.action}</p>
                    {rec.rationale && <p className="text-[11px] text-muted-foreground mt-0.5"><span className="text-foreground">Why:</span> {rec.rationale}</p>}
                    {rec.expected_impact && <p className="text-[11px] text-muted-foreground"><span className="text-foreground">Expected impact:</span> {rec.expected_impact}</p>}
                    {a.scenario && (
                      <div className="mt-1.5 rounded-lg border border-violet-500/30 bg-violet-500/5 p-2" data-testid={`appr-scenario-${a.approval_id}`}>
                        <div className="flex items-center gap-1.5 text-[11px] text-violet-700 dark:text-violet-300 font-medium">
                          <FlaskConical className="w-3 h-3" /> Simulated rationale — {a.scenario.name}
                          <span className="ml-auto text-foreground">{a.scenario.headline}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                          {(a.scenario.signals || []).filter((s) => s.before || s.after).map((s) => (
                            <span key={s.signal_type}>{s.title}: <span className="text-foreground">{s.before} → {s.after}</span> ({s.firing_before}→{s.firing_after} firing)</span>
                          ))}
                        </div>
                        {a.scenario.top_lever && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground">Biggest lever: <span className="text-foreground">{a.scenario.top_lever.label} ({a.scenario.top_lever.value > 0 ? '+' : ''}{a.scenario.top_lever.value}%)</span></div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-1">
                      <span>Triggered by</span>
                      <span className="text-foreground">{a.trigger_match?.member} {a.trigger_match?.display}</span>
                      <span>· requested by <span className="text-foreground">{a.requested_by?.name}</span></span>
                      {a.decided_by?.name && <span>· decided by <span className="text-foreground">{a.decided_by.name}</span></span>}
                    </div>
                  </div>
                </div>

                {canManage && a.status === 'pending_approval' && (
                  isRequester ? (
                    <p className="text-[11px] text-amber-500 flex items-center gap-1" data-testid={`appr-foureyes-${a.approval_id}`}>
                      <Lock className="w-3 h-3" /> You requested this — a different steward must approve it (four-eyes).
                    </p>
                  ) : (
                    <div className="flex items-center gap-2 pt-1" data-testid={`appr-decide-${a.approval_id}`}>
                      <input value={notes[a.approval_id] || ''} onChange={(e) => setNotes((n) => ({ ...n, [a.approval_id]: e.target.value }))}
                        placeholder="Decision note (required to reject)"
                        className="flex-1 h-7 px-2 rounded border border-border/40 bg-background text-xs" data-testid={`appr-note-${a.approval_id}`} />
                      <Button size="sm" onClick={() => decide(a, 'approve')} disabled={busy.has(`${a.approval_id}-approve`)}
                        className="h-7 text-[11px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`appr-approve-${a.approval_id}`}>
                        <Check className="w-3 h-3" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => decide(a, 'reject')} disabled={busy.has(`${a.approval_id}-reject`)}
                        className="h-7 text-[11px] gap-1 border-rose-500/40 text-rose-600 dark:text-rose-300" data-testid={`appr-reject-${a.approval_id}`}>
                        <X className="w-3 h-3" /> Reject
                      </Button>
                    </div>
                  )
                )}

                {canManage && a.status === 'approved' && (
                  <div className="flex items-center gap-2 pt-1" data-testid={`appr-action-row-${a.approval_id}`}>
                    <input value={notes[a.approval_id] || ''} onChange={(e) => setNotes((n) => ({ ...n, [a.approval_id]: e.target.value }))}
                      placeholder="How it was carried out"
                      className="flex-1 h-7 px-2 rounded border border-border/40 bg-background text-xs" data-testid={`appr-action-note-${a.approval_id}`} />
                    <Button size="sm" onClick={() => action(a)} disabled={busy.has(`${a.approval_id}-action`)}
                      className="h-7 text-[11px] gap-1" data-testid={`appr-action-${a.approval_id}`}>
                      <PlayCircle className="w-3 h-3" /> Mark actioned
                    </Button>
                  </div>
                )}

                {a.decision_note && <p className="text-[11px] text-muted-foreground italic">Decision: “{a.decision_note}”</p>}

                <button onClick={() => toggleExpand(a.approval_id)} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                  data-testid={`appr-audit-toggle-${a.approval_id}`}>
                  {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <History className="w-3 h-3" /> Audit ({(a.audit || []).length})
                </button>
                {open && (
                  <div className="pl-4 border-l border-border/40 space-y-1" data-testid={`appr-audit-${a.approval_id}`}>
                    {(a.audit || []).map((h, i) => (
                      <div key={i} className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-[8px] ${STATUS[h.event === 'submitted' ? 'pending_approval' : h.event]?.cls || 'border-border/40'}`}>{h.event}</Badge>
                        <span className="text-foreground">{h.by?.name}</span>
                        {h.note && <span>“{h.note}”</span>}
                        {h.at && <span className="opacity-60">{new Date(h.at).toLocaleString()}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { RecommendationApprovalsPage };
